// Pure helpers for the result timeline: everything the AI reviewer pinned to
// a moment in the video (checklist evidence, flagged outliers, frame notes)
// placed along the video's length. Built only from fields the API returns.
import { FEEDBACK, RESULT, RESULT_TONE } from './qcFormat';

const TIME_PART = /^\d+(\.\d+)?$/;
const text = (value) => (typeof value === 'string' ? value : '');

// "m:ss" / "h:mm:ss" string, or seconds as a number. null if neither.
export function toSeconds(value) {
    if (typeof value === 'number') return Number.isFinite(value) && value >= 0 ? value : null;
    if (typeof value !== 'string') return null;
    const parts = value.trim().split(':');
    if (parts.length < 2 || parts.length > 3 || !parts.every((p) => TIME_PART.test(p))) return null;
    return parts.reduce((total, p) => total * 60 + Number(p), 0);
}

const pad = (n) => String(n).padStart(2, '0');

export function fmtTs(seconds) {
    const s = Math.max(0, Math.floor(seconds || 0));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return h ? `${h}:${pad(m)}:${pad(s % 60)}` : `${m}:${pad(s % 60)}`;
}

// Lower is more serious. Markers and the starting moment use this order.
export const TONE_RANK = { bad: 0, flag: 1, warn: 2, good: 3 };

const at = (ts) => ({ ts: text(ts) || (typeof ts === 'number' ? fmtTs(ts) : ''), t: toSeconds(ts) });

const resultEvents = (results) => (results || []).flatMap((r) => (Array.isArray(r?.timestamps) ? r.timestamps : [])
    .map(at)
    .filter((p) => p.t != null)
    .map(({ ts, t }) => ({
        id: `result-${r.item_id}-${ts}`, kind: 'result', t, ts,
        tone: RESULT_TONE[r.status] || 'warn',
        label: (RESULT[r.status] || RESULT.unclear).label,
        note: text(r.requirement), critical: Boolean(r.critical),
    })));

const outlierEvents = (outliers) => (outliers || [])
    .map((o, i) => ({ ...at(o?.ts), id: `outlier-${i}`, kind: 'outlier', tone: 'flag', label: 'Flagged', severity: text(o?.severity), note: text(o?.note) }))
    .filter((e) => e.t != null);

const feedbackEvents = (feedback) => (feedback || [])
    .map((f, i) => {
        const tone = FEEDBACK[f?.status] ? f.status : 'warn';
        return { ...at(f?.ts), id: `feedback-${i}`, kind: 'feedback', tone, label: FEEDBACK[tone].label, note: text(f?.note) };
    })
    .filter((e) => e.t != null);

// Every pinned item as { id, kind, t, ts, tone, label, note }, in time order
// and, at the same time, most serious first.
export function buildEvents(check) {
    if (!check) return [];
    const all = [...resultEvents(check.results), ...outlierEvents(check.outliers), ...feedbackEvents(check.frame_feedback)];
    return [...all].sort((a, b) => a.t - b.t || TONE_RANK[a.tone] - TONE_RANK[b.tone]);
}

export const worstTone = (events) => events.reduce((worst, e) => (TONE_RANK[e.tone] < TONE_RANK[worst] ? e.tone : worst), 'good');

// One marker per moment: events at the same second share it.
export function groupMoments(events) {
    const byTime = events.reduce((map, e) => new Map(map).set(e.t, [...(map.get(e.t) || []), e]), new Map());
    return [...byTime.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([t, list]) => ({ t, ts: list[0].ts, events: list, tone: worstTone(list) }));
}

// Filters for the frame-by-frame view. Each keeps the notes in its tones;
// "With issues" is everything not marked met or good. The labels double as
// the colour legend.
export const FRAME_FILTERS = [
    { id: 'all', label: 'All', tones: null },
    { id: 'issues', label: 'With issues', tones: ['bad', 'flag', 'warn'] },
    { id: 'bad', label: 'Missed / problem', tones: ['bad'] },
    { id: 'warn', label: 'Unclear / check', tones: ['warn'] },
    { id: 'flag', label: 'Flagged', tones: ['flag'] },
    { id: 'good', label: 'Met / good', tones: ['good'] },
];

export const keepsEvent = (filter, event) => !filter?.tones || filter.tones.includes(event.tone);

// The moments a filter keeps, each with only the notes it keeps and its
// marker coloured by those.
export const filterMoments = (moments, filter) => (!filter?.tones ? moments : moments
    .map((m) => ({ ...m, events: m.events.filter((e) => keepsEvent(filter, e)) }))
    .filter((m) => m.events.length)
    .map((m) => ({ ...m, tone: worstTone(m.events) })));

// Where the result view opens: the most serious moment, earliest first.
export const pickStartMoment = (moments) => moments.reduce(
    (best, m) => (!best || TONE_RANK[m.tone] < TONE_RANK[best.tone] ? m : best),
    null,
);

export const frameTime = (frame) => (typeof frame?.t === 'number' ? frame.t : toSeconds(frame?.ts));

export function nearestFrameIndex(frames, t) {
    if (!frames?.length || t == null) return -1;
    const gap = (f) => Math.abs((frameTime(f) ?? Infinity) - t);
    return frames.reduce((best, f, i) => (gap(f) < gap(frames[best]) ? i : best), 0);
}

// Seconds the timeline spans: the reported duration, stretched to fit any
// frame or note that lands after it.
export function timelineLength(video, frames = [], events = []) {
    const reported = toSeconds(video?.format_meta?.duration) || 0;
    const times = [...frames.map(frameTime), ...events.map((e) => e.t)].filter((t) => t != null);
    return Math.max(reported, ...times, 0);
}
